import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as ActivityIcon } from '../../../../assets/icons/activety-icon.svg';
import { ReactComponent as CardPosIcon } from '../../../../assets/icons/card-pos-icon.svg';
import { ReactComponent as FleshIcon } from '../../../../assets/icons/flash-icon.svg';
import { ReactComponent as MoreIcon } from '../../../../assets/icons/more-icon.svg';
import { ReactComponent as ReceiptIcon } from '../../../../assets/icons/receipt-icon.svg';
import { ReactComponent as ResetPasswordIcon } from '../../../../assets/icons/reset-password-v1-icon.svg';
import { ReactComponent as PenaltyManagementFillIcon } from '../../../../assets/icons/sidebar/penalty-management-fill.svg';
import profile from '../../../../assets/images/profiles/profile.jpg';
import { useAppBranding } from '../../../../config/branding';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { fetchBasicInformations } from '../../../../redux/slices/EndConsumerSlice';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import MyButton from '../../../../shared-components/button/Button';
import DropDownMenu from '../../../../shared-components/drop-down/DropDownMenu';
import ConfirmBox from '../../../../shared-components/modal-box/ConfirmBox';
import ModalBox from '../../../../shared-components/modal-box/ModalBox';
import SuccessNotification from '../../../../shared-components/success-notification/SuccessNotification';
import MapSidePanel from '../../../../shared-components/table-side-panel/MapSidePanel';
import SidePaneModel from '../../../../shared-components/table-side-panel/SidePaneModel';
import ToFixedValue from '../../../../shared-functions/ToFixedValue';
import userApi from '../../../../utils/api/end-consumer';
import userApiV2 from '../../../../utils/api/end-consumer-v2';
// import DebitForm from '../FormComponents/DebitForm';
// import RefundForm from '../FormComponents/RefundForm';
import ActiveChargingSession from '../SidePaneComponents/ActiveChargingSession/ActiveChargingSession';
import MyVehicle from '../SidePaneComponents/MyVehicle/MyVehicle';
import PaymentMethod from '../SidePaneComponents/PaymentMethod/PaymentMethod';
import WalletHistory from '../SidePaneComponents/WalletHistory/WalletHistory';

import ActivityTimeline from './ActivityTimeline';
import AddWallet from './AddWallet';
import PersonalChargers from './PersonalCharger';

interface MenuList {
  id: string;
  content: JSX.Element;
  allow: boolean;
  function: () => void;
  disabled: boolean;
}

/**
 * Component to display end consumer profile
 * @returns
 */
export default function ProfileConsumer() {
  const branding = useAppBranding();
  const { t, i18n } = useTranslation('endConsumer');
  const dispatch = useAppDispatch();
  const [openStatus, setOpenStatus] = useState<boolean>(false);
  const [consumerStatus, setConsumerStatus] = useState<string>('');
  const { loading, selectedEndConsumer, basicInformation } = useAppSelector(
    (state) => state.endConsumerType,
  );

  const [modalView, setModalView] = useState({
    status: false,
    title: t('addAccount'),
    header: true,
    content: <div />,
    rightCorner: '',
  });
  const [confirmBox, setConfirmBox] = useState({
    status: false,
    title: '',
    subTitle: '',
    submitLabel: t('ok'),
    /**
     * Demo function
     */
    conformSubmit: () => {},
  });

  const [isModelOpen, setIsModelOpen] = useState<boolean>(false);
  const [modelData, setModelData] = useState({
    title: '',
    content: <div />,
  });

  const statsConfig = [
    {
      key: 'energy',
      label: t('lifetimeEnergyConsumption'),
      value: basicInformation?.totalPowerConsumedInKwh || 0,
      unit: 'Kw',
      icon: <FleshIcon />,
    },
    {
      key: 'spend',
      label: t('lifetimeSpend'),
      value: basicInformation?.totalRevenue || 0,
      unit: branding.text.currency,
      icon: <CardPosIcon />,
    },
    {
      key: 'penaltyPaid',
      label: t('penaltyChargesPaid'),
      value: basicInformation?.penaltySummary.penaltyChargePaid || 0,
      unit: branding.text.currency,
      icon: <ReceiptIcon />,
    },
    {
      key: 'penaltyCount',
      label: t('penaltyCounts'),
      value: basicInformation?.penaltySummary.penaltyCount || 0,
      icon: <PenaltyManagementFillIcon />,
    },
  ];

  /**
   * Fetch Users list function
   */
  const fetchData = (userCustomId: string) => {
    if (!loading) {
      dispatch(fetchBasicInformations({ userCustomId }));
    }
  };

  /**
   * Sends a password reset link to the user's email for a specific consumer.
   *
   * @param {string} currentData - The ID of the consumer whose password is to be reset.
   */
  const confirmResetPassword = (currentData: string) => {
    userApi
      .resetPassword(currentData, { submissionStatus: 'userCustomId' })
      .then(() => {
        setOpenStatus(true);
        setConfirmBox((previous) => ({ ...previous, status: false }));
        setModalView((previous) => ({ ...previous, status: false }));
        dispatch(
          setNotificationMessage({
            message: `${t('passwordResetSuccess')}: ${currentData}.`,
            status: true,
            type: 'success',
            code: 200,
          }),
        );
      })
      .catch((error) => {
        dispatch(
          setNotificationMessage({
            message:
              error?.response?.data?.message ||
              `Password reset failed for Consumer ID: ${currentData}.`,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Modal box close function
   */
  const closeModal = () => {
    setModalView({
      status: false,
      title: '',
      header: false,
      content: <div />,
      rightCorner: '',
    });
  };

  /**
   * Displays a confirmation prompt to reset the password for a specific consumer.
   *
   * @param {string} currentData - The ID of the consumer whose password is to be reset.
   */
  const resetPasswordFunction = () => {
    if (selectedEndConsumer?.userCustomId) {
      setConfirmBox({
        status: true,
        title: t('resetPasswordTitle'),
        subTitle: t('resetPasswordMessage'),
        /**
         * Triggers the password reset process for the specified consumer.
         *
         * @returns {void}
         */
        conformSubmit: () => confirmResetPassword(selectedEndConsumer?.userCustomId), // ✅ Now correctly calls the function
        submitLabel: t('proceed'),
      });
    }
  };
  /**
   * Open Activity Log side panel
   */
  const handleLogActivity = () => {
    setIsModelOpen(true);
    setModelData({
      title: t('activityLog'),
      content: <ActivityTimeline />,
    });
  };

  /**
   * Favorite Station
   */
  const handleFavoriteStation = () => {
    setIsModelOpen(true);
    setModelData({
      title: t('favoriteStations'),
      content: <MapSidePanel />,
    });
  };

  /**
   * Personal Charger
   */
  const handlePersonalCharger = () => {
    setIsModelOpen(true);
    setModelData({
      title: t('personalCharger'),
      content: <PersonalChargers />,
    });
  };

  /**
   * Open Vehicle Details side panel
   */
  const handleOpenVehicleDetails = () => {
    setIsModelOpen(true);
    setModelData({
      title: t('vehicle'),
      content: <MyVehicle />,
    });
  };

  /**
   * Open Payment Method side panel
   */
  const handleOpenPaymentMethod = () => {
    setIsModelOpen(true);
    setModelData({
      title: t('paymentMethod'),
      content: <PaymentMethod />,
    });
  };

  // /**
  //  * handle Debit
  //  */
  // const handleDebit = () => {
  //   setIsModelOpen(true);
  //   setModelData({
  //     title: 'Debit Form',
  //     content: <DebitForm />,
  //   });
  // };

  // /**
  //  * handle Refund
  //  */
  // const handleRefund = () => {
  //   setIsModelOpen(true);
  //   setModelData({
  //     title: 'Refund Form',
  //     content: <RefundForm />,
  //   });
  // };

  /**
   * Open Wallet History side panel
   */
  const renderWalletHistory = () => {
    setIsModelOpen(true);
    setModelData({
      title: t('walletHistory'),
      content: <WalletHistory />,
    });
  };

  const [isEditMode, setIsEditMode] = useState(false);

  const [editForm, setEditForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    countryCode: '',
    accountStatus: '',
  });

  const [originalForm, setOriginalForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    countryCode: '',
  });

  /**
   * Populate edit form when entering edit mode
   */
  const handleProfileEditInformation = async () => {
    if (!selectedEndConsumer?.userCustomId) return;

    try {
      const response = await userApiV2.fetchBasicProfile(selectedEndConsumer.userCustomId);
      setOriginalForm(response.data);

      setEditForm(response.data);
      setConsumerStatus(response.data.accountStatus);
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message || t('updateFailed'),
          status: true,
          type: 'error',
          code: error?.response?.data?.code,
        }),
      );
    }
  };

  /**
   * Handle input change
   */
  const handleChange = useCallback((key: keyof any, value: string) => {
    setEditForm((previous) => ({ ...previous, [key]: value }));
  }, []);
  /**
   * Save edited profile or update account status
   */
  const handleSave = useCallback(
    async (actionType: 'edit' | 'VIP' | 'ONHOLD' | 'BLOCKED' | 'RESUMED') => {
      if (!selectedEndConsumer?.userCustomId) return;

      let payload: Record<string, string> = {};

      switch (actionType) {
        case 'edit': {
          const changedPayload: Record<string, string> = {};

          if (editForm.firstName !== originalForm.firstName) {
            changedPayload.firstName = editForm.firstName;
          }

          if (editForm.lastName !== originalForm.lastName) {
            changedPayload.lastName = editForm.lastName;
          }

          if (editForm.email !== originalForm.email) {
            changedPayload.email = editForm.email;
          }

          if (editForm.phoneNumber !== originalForm.phoneNumber) {
            changedPayload.phoneNumber = editForm.phoneNumber;
          }

          if (editForm.countryCode !== originalForm.countryCode) {
            changedPayload.countryCode = editForm.countryCode;
          }

          payload = changedPayload;
          break;
        }

        case 'VIP':
          payload = {
            accountStatus: 'VIP',
          };
          break;

        case 'ONHOLD':
          payload = {
            accountStatus: 'ONHOLD',
          };
          break;

        case 'BLOCKED':
          payload = {
            accountStatus: 'BLOCKED',
          };
          break;

        case 'RESUMED':
          payload = {
            accountStatus: 'RESUMED',
          };
          break;

        default:
          return;
      }

      try {
        if (actionType === 'edit' && Object.keys(payload).length === 0) {
          setIsEditMode(false);
          return;
        }

        await userApiV2.editBasicInformation(selectedEndConsumer.userCustomId, payload);
        setConsumerStatus(actionType);
        handleProfileEditInformation();
        dispatch(
          setNotificationMessage({
            message: t('consumerUpdatedSuccessfully'),
            status: true,
            type: 'success',
            code: 200,
          }),
        );

        setIsEditMode(false);

        // Refresh latest profile data
        dispatch(fetchBasicInformations({ userCustomId: selectedEndConsumer.userCustomId }));
      } catch (error: any) {
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message || t('updateFailed'),
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      }
    },
    [dispatch, editForm, originalForm, selectedEndConsumer],
  );

  /**
   * Populate edit form when entering edit mode
   */
  const startEditMode = useCallback(() => {
    setIsEditMode(true);
  }, [basicInformation]);
  const isHold = consumerStatus === 'ONHOLD';
  const isPaused = consumerStatus === 'BLOCKED';

  const menuList: MenuList[] = [
    {
      id: 'toggle-status',
      content: <span>{isPaused ? t('resume') : t('pause')}</span>,
      allow: true,
      disabled: isHold, // ONHOLD disables everything
      /**
       * Resume || pause
       * @returns
       */
      function: () => handleSave(isPaused ? 'RESUMED' : 'BLOCKED'),
    },
    {
      id: 'hold',
      content: <span>{t('hold')}</span>,
      allow: true,
      disabled: isHold || isPaused, // Disabled when ONHOLD or BLOCKED
      /**
       * ONHOLD
       * @returns
       */
      function: () => handleSave('ONHOLD'),
    },
    {
      id: 'vip',
      content: <span>{t('markAsVip')}</span>,
      allow: true,
      disabled: isHold || isPaused, // Disabled when ONHOLD or BLOCKED
      /**
       * ONHOLD
       * @returns
       */
      function: () => handleSave('VIP'),
    },
    {
      id: 'edit',
      content: <span>{t('edit')}</span>,
      allow: true,
      disabled: isHold, // Disabled only when ONHOLD
      /**
       * ONHOLD
       * @returns
       */
      function: () => startEditMode(),
    },
  ];

  /**
   * Handle open modal
   */
  const handleOpenModal = () => {
    const endConsumerId = selectedEndConsumer?.userCustomId;
    if (!endConsumerId) return;
    setModalView({
      status: true,
      title: t('addMoney'),
      content: <AddWallet endConsumerId={endConsumerId} closeModal={closeModal} />,
      header: true,
      rightCorner: '',
    });
  };

  useEffect(() => {
    if (selectedEndConsumer?.userCustomId) {
      fetchData(selectedEndConsumer.userCustomId);
      handleProfileEditInformation();
    }
  }, [selectedEndConsumer]);

  return (
    <div className="ec-profile" dir={i18n.dir()}>
      {isModelOpen && (
        <SidePaneModel
          open={isModelOpen}
          title={modelData.title}
          content={modelData.content}
          closePanel={() => setIsModelOpen(false)}
        />
      )}
      <div className="success-notification">
        {openStatus && (
          <SuccessNotification
            title={t('passwordResetSuccessNotification')}
            description=""
            notificationCloseTimer={3000}
            onNotificationClose={() => {
              setOpenStatus(false); // Hide the notification after the timer or when closed
            }}
          />
        )}
      </div>
      <ModalBox
        open={modalView.status}
        title={modalView.title}
        content={modalView.content}
        closeModal={closeModal}
        header={modalView.header}
        rightCorner={modalView.rightCorner}
      />
      <ConfirmBox
        open={confirmBox.status}
        title={confirmBox.title}
        subTitle={confirmBox.subTitle}
        conformSubmit={confirmBox.conformSubmit}
        closeModal={() => setConfirmBox((previous) => ({ ...previous, status: false }))}
        submitLabel={confirmBox.submitLabel}
      />
      <div className="profile-header-wrap">
        {consumerStatus && (
          <div className="consumer-status-container">
            <div className={`consumer-status ${consumerStatus}`}>
              {consumerStatus === 'VIP' ? (
                <p className="status-value">{consumerStatus.toUpperCase()}</p>
              ) : (
                <p className="status-value">
                  {consumerStatus.charAt(0).toUpperCase() + consumerStatus.slice(1)}
                </p>
              )}
            </div>
          </div>
        )}
        <div className="profile-header-title">
          <p className="title">{t('basicInformation')}</p>
          <div className="card-header-item-icon">
            {menuList.some((item) => item.allow) && (
              <DropDownMenu list={menuList} icon={<MoreIcon />} />
            )}
          </div>
        </div>
      </div>

      <div className="card-header">
        <div className="card-header-item-image">
          {basicInformation?.user?.profileImage?.[0]?.signedUrl ? (
            <img
              src={basicInformation?.user?.profileImage?.[0]?.signedUrl}
              alt="profile"
              className="selected-image"
            />
          ) : (
            <img src={profile} alt="profile" />
          )}
        </div>
        <div className="card-header-item-title">
          <div className="title">
            {isEditMode ? (
              <>
                <div>
                  <input
                    style={{ width: '50%' }}
                    className="inline-input"
                    value={editForm.firstName}
                    onChange={(event) => handleChange('firstName', event.target.value)}
                    placeholder={t('firstName')}
                  />
                </div>
                <div>
                  <input
                    style={{ width: '50%' }}
                    className="inline-input"
                    value={editForm.lastName}
                    onChange={(even) => handleChange('lastName', even.target.value)}
                    placeholder={t('lastName')}
                  />
                </div>
              </>
            ) : (
              <>
                {editForm?.firstName} {editForm?.lastName}
              </>
            )}{' '}
          </div>
          <div className="sub-title">{basicInformation?.user.userCustomId}</div>
        </div>
      </div>

      {/* =================== WALLET =================== */}
      <div className="ec-profile-wallet">
        <div className="label">{t('walletBalance')}</div>
        <div className="value">
          {ToFixedValue(basicInformation?.walletBalance) || 0} {branding.text.currency}
        </div>
      </div>
      {/* =================== QUICK ACTIONS =================== */}
      <div className="actions">
        <MyButton
          label={t('activityLog')}
          buttonType="submit"
          onClickFunc={() => handleLogActivity()}
          svgIcon={<ActivityIcon />}
        />
        <MyButton
          label={t('resetPassword')}
          buttonType="submit"
          onClickFunc={() => resetPasswordFunction()}
          svgIcon={<ResetPasswordIcon />}
        />
        {/* <MyButton
          label="Send OTP"
          buttonType="submit"
          onClickFunc={() => resetPasswordFunction()}
        /> */}
      </div>
      {/* =================== INFO GRID =================== */}
      <div className="ec-profile-info">
        <div className="ec-info">
          <p className="title">{t('status')}</p>
          <div className={`values ${basicInformation?.user.isActive ? 'success' : 'error'}`}>
            <div className="color-circle" />
            <p>{basicInformation?.user.isActive ? t('active') : t('inactive')}</p>
          </div>
        </div>

        <div className="ec-info">
          <p className="title">{t('rfidNo')}</p>
          <p className="values">
            {' '}
            {basicInformation?.rfidCards?.map((card) => card.rfidNo).join(', ') || 0}
          </p>
        </div>

        <div className="ec-info">
          <p className="title">{t('email')}</p>
          {isEditMode ? (
            <div>
              <input
                style={{ width: '70%' }}
                className="inline-input"
                value={editForm.email}
                placeholder={t('email')}
                onChange={(even) => handleChange('email', even.target.value)}
              />
            </div>
          ) : (
            <p className="values">{editForm?.email ?? 'N/A'}</p>
          )}{' '}
        </div>

        <div className="ec-info">
          <p className="title">{t('phone')}</p>
          {isEditMode ? (
            <div>
              <div>
                <input
                  style={{ width: '50%' }}
                  className="inline-input"
                  value={editForm.countryCode}
                  placeholder={t('countryCode')}
                  onChange={(even) => handleChange('countryCode', even.target.value)}
                />
              </div>

              <div>
                <input
                  style={{ width: '90%' }}
                  className="inline-input"
                  value={editForm.phoneNumber}
                  placeholder={t('phoneNumber')}
                  onChange={(even) => handleChange('phoneNumber', even.target.value)}
                />
              </div>
            </div>
          ) : (
            <p className="values">{editForm?.phoneNumber ?? 'N/A'}</p>
          )}{' '}
        </div>
        {isEditMode && (
          <>
            <MyButton
              label={t('save')}
              buttonType="submit"
              onClickFunc={() => {
                handleSave('edit');
              }}
            />
            <MyButton
              label={t('cancel')}
              buttonType="submit"
              onClickFunc={() => setIsEditMode(false)}
            />
          </>
        )}
        <div className="ec-info">
          <p className="title">{t('personalCharger')}</p>
          <div
            className={`values hyper-link ${basicInformation?.personalChargingPointCount ? '' : 'disabled'}`}
            role="presentation"
            onClick={handlePersonalCharger}>
            <p className="counts">
              {basicInformation?.personalChargingPointCount
                ? `${basicInformation?.personalChargingPointCount}`
                : 0}
            </p>
          </div>
        </div>
        <div className="ec-info">
          <p className="title">{t('favoriteStations')}</p>
          <div className="ec-inline">
            {/* <p className="values">Station 1</p> */}
            <div
              className={`values hyper-link ${basicInformation?.favouriteStationCount ? '' : 'disabled'}`}
              role="presentation"
              onClick={handleFavoriteStation}>
              <p className="counts">
                {basicInformation?.favouriteStationCount
                  ? `${basicInformation?.favouriteStationCount}`
                  : 0}
              </p>
            </div>
          </div>
        </div>

        <div className="ec-info">
          <p className="title">{t('paymentMethod')}</p>
          <div className="ec-inline">
            {/* <p className="values">Credit Card</p> */}
            <p
              className={`values hyper-link ${basicInformation?.hyperpayCustomerCard ? '' : 'disabled'}`}
              role="presentation"
              onClick={handleOpenPaymentMethod}>
              <p className="counts">
                {basicInformation?.hyperpayCustomerCard
                  ? `${basicInformation.hyperpayCustomerCard}`
                  : 0}
              </p>
            </p>
          </div>
        </div>

        <div className="ec-info">
          <p className="title">{t('vehicle')}</p>
          <div className="ec-inline">
            <p className="values hyper-link" role="presentation" onClick={handleOpenVehicleDetails}>
              {basicInformation?.vehicles?.[0]?.manufacturer || ''}
            </p>
            <p
              className={`values hyper-link ${Number(basicInformation?.vehicles?.length) > 1 ? '' : 'disabled'}`}
              role="presentation"
              onClick={handleOpenVehicleDetails}>
              <p className="counts">
                {basicInformation?.vehicles.length
                  ? `${Number(basicInformation?.vehicles.length) > 1 ? `+${Number(basicInformation?.vehicles.length) - 1}` : ''}`
                  : 0}
              </p>
            </p>
          </div>
        </div>
      </div>

      {/* =================== STATS =================== */}
      <div className="stat-card-content">
        {statsConfig.map((item) => (
          <div key={item.key} className="stat-card">
            <div className="icon-wrapper">{item.icon}</div>

            <div className="content">
              <p className="label">{item.label}</p>

              <p className="value">
                {item.value}
                {item.unit && <span className="unit"> {item.unit}</span>}
              </p>
            </div>
          </div>
        ))}
      </div>
      {/* <div className="actions"> */}
      {/* <MyButton label="Debit" buttonType="submit" onClickFunc={() => handleDebit()} />
        <MyButton label="Refund" buttonType="submit" onClickFunc={() => handleRefund()} /> */}
      <MyButton label={t('walletHistory')} buttonType="submit" onClickFunc={renderWalletHistory} />
      <MyButton label={t('addMoney')} buttonType="submit" onClickFunc={handleOpenModal} />
      <ActiveChargingSession />
    </div>
  );
}
